import React, { useState, useCallback, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import DraggableItem from './DraggableItem';
import DropZone from './DropZone';
import {
  PlusIcon,
  PhotoIcon,
  DocumentTextIcon,
  SwatchIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

interface Item {
  id: string;
  type: 'image' | 'text' | 'sticky';
  content: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  color?: string;
  fontSize?: number;
  fontFamily?: string;
  layer: number;
  author?: string;
  timestamp?: string;
}

const CARD_WIDTH = 800;
const CARD_HEIGHT = 1200;

const TEMPLATES: Array<{
  type: 'image' | 'text' | 'sticky';
  content: string;
  size: { width: number; height: number };
  color?: string;
  fontSize?: number;
  fontFamily?: string;
}> = [
  {
    type: 'text',
    content: 'Inspiration',
    size: { width: 200, height: 100 },
    color: '#000000',
    fontSize: 24,
    fontFamily: 'Inter',
  },
  {
    type: 'text',
    content: 'Mood Board',
    size: { width: 200, height: 100 },
    color: '#4A90E2',
    fontSize: 32,
    fontFamily: 'Inter',
  },
  {
    type: 'sticky',
    content: 'Add your ideas here...',
    size: { width: 150, height: 120 },
    color: '#FFD93D',
    fontSize: 14,
    fontFamily: 'Inter',
  },
  {
    type: 'sticky',
    content: 'Design notes...',
    size: { width: 150, height: 120 },
    color: '#FF6B6B',
    fontSize: 14,
    fontFamily: 'Inter',
  },
  {
    type: 'sticky',
    content: 'Color palette ideas',
    size: { width: 150, height: 120 },
    color: '#4ECDC4',
    fontSize: 14,
    fontFamily: 'Inter',
  },
];

const MoodBoard: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [showCollaborators, setShowCollaborators] = useState(true);
  const [showComments, setShowComments] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Mock collaborators data
  const collaborators = [
    { id: 1, name: 'Sarah Chen', avatar: 'SC', online: true, color: '#FF6B6B' },
    { id: 2, name: 'Mike Johnson', avatar: 'MJ', online: true, color: '#4ECDC4' },
    { id: 3, name: 'Emma Davis', avatar: 'ED', online: false, color: '#45B7D1' },
  ];

  // Only allow items to be dropped within the card area
  const handleDrop = (item: Item, position: { x: number; y: number }) => {
    const snappedPosition = {
      x: Math.max(0, Math.min(Math.round(position.x / 20) * 20, CARD_WIDTH - (item.size?.width || 100))),
      y: Math.max(0, Math.min(Math.round(position.y / 20) * 20, CARD_HEIGHT - (item.size?.height || 100))),
    };
    setItems(prevItems =>
      prevItems.map(i =>
        i.id === item.id ? { ...i, position: snappedPosition } : i
      )
    );
  };

  const addItem = (type: 'image' | 'text' | 'sticky', template?: any) => {
    const newItem: Item = {
      id: Date.now().toString(),
      type,
      content: template?.content || (type === 'text' ? 'Double click to edit' : type === 'sticky' ? 'Add your note...' : ''),
      position: { x: 50, y: 50 },
      size: template?.size || { width: type === 'text' ? 200 : type === 'sticky' ? 150 : 150, height: type === 'text' ? 100 : type === 'sticky' ? 120 : 150 },
      color: template?.color || (type === 'sticky' ? '#FFD93D' : '#000000'),
      fontSize: template?.fontSize || (type === 'sticky' ? 14 : 16),
      fontFamily: template?.fontFamily || 'Inter',
      layer: items.length,
      author: 'You',
      timestamp: new Date().toLocaleTimeString(),
    };
    setItems(prev => [...prev, newItem]);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          addItem('image', {
            content: event.target.result,
            size: { width: 300, height: 200 },
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus('saving');
    
    // Simulate save operation
    setTimeout(() => {
    const data = JSON.stringify(items);
    localStorage.setItem('moodboard', data);
      setSaveStatus('saved');
      setIsSaving(false);
      
      // Reset status after 2 seconds
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 1000);
  };

  const handleLoad = () => {
    const data = localStorage.getItem('moodboard');
    if (data) {
      setItems(JSON.parse(data));
    }
  };

  const handleItemSelect = (id: string) => {
    setSelectedItem(id);
  };

  const handleItemUpdate = (id: string, updates: Partial<Item>) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === id ? { ...item, ...updates } : item
      )
    );
  };

  const handleLayerChange = (id: string, direction: 'up' | 'down') => {
    setItems(prevItems => {
      const itemIndex = prevItems.findIndex(item => item.id === id);
      if (itemIndex === -1) return prevItems;
      const newItems = [...prevItems];
      const item = newItems[itemIndex];
      if (direction === 'up' && itemIndex < newItems.length - 1) {
        newItems[itemIndex] = newItems[itemIndex + 1];
        newItems[itemIndex + 1] = item;
      } else if (direction === 'down' && itemIndex > 0) {
        newItems[itemIndex] = newItems[itemIndex - 1];
        newItems[itemIndex - 1] = item;
      }
      return newItems.map((item, index) => ({ ...item, layer: index }));
    });
  };

  // Delete selected item with Delete/Backspace
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.key === 'Delete' || e.key === 'Backspace') && selectedItem) {
      setItems(prevItems => prevItems.filter(item => item.id !== selectedItem));
      setSelectedItem(null);
    }
  }, [selectedItem]);

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        {/* Enhanced Sidebar */}
        <div className="w-80 bg-white/80 backdrop-blur-sm border-r border-gray-200 flex flex-col p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Mood Board</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                title="Save"
              >
                {saveStatus === 'saving' ? (
                  <ArrowPathIcon className="w-5 h-5 text-blue-600 animate-spin" />
                ) : saveStatus === 'saved' ? (
                  <CheckIcon className="w-5 h-5 text-green-600" />
                ) : (
                  <CloudArrowUpIcon className="w-5 h-5 text-gray-600" />
                )}
              </button>
              <button
                onClick={handleLoad}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Load"
              >
                <ArrowPathIcon className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Add Elements Section */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Add Elements</h3>
            <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => addItem('image')}
                className="flex flex-col items-center p-4 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-sm"
          >
                <PhotoIcon className="w-6 h-6 mb-2" />
                <span className="text-sm font-medium">Image</span>
          </button>
          <button
            onClick={() => addItem('text')}
                className="flex flex-col items-center p-4 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-sm"
              >
                <DocumentTextIcon className="w-6 h-6 mb-2" />
                <span className="text-sm font-medium">Text</span>
              </button>
              <button
                onClick={() => addItem('sticky')}
                className="flex flex-col items-center p-4 bg-gradient-to-br from-yellow-400 to-orange-500 text-white rounded-lg hover:from-yellow-500 hover:to-orange-600 transition-all duration-200 shadow-sm"
              >
                <SwatchIcon className="w-6 h-6 mb-2" />
                <span className="text-sm font-medium">Sticky Note</span>
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center p-4 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-sm"
          >
                <PhotoIcon className="w-6 h-6 mb-2" />
                <span className="text-sm font-medium">Upload</span>
          </button>
            </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            className="hidden"
          />
          </div>

          {/* Templates Section */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Quick Templates</h3>
            <div className="space-y-2">
            {TEMPLATES.map((template, index) => (
              <button
                key={index}
                onClick={() => addItem(template.type, template)}
                  className="w-full p-3 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors text-left"
              >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{template.content}</span>
                    <span className="text-xs text-gray-500 capitalize">{template.type}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Collaboration Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Collaboration</h3>
              <button
                onClick={() => setShowCollaborators(!showCollaborators)}
                className="p-1 rounded hover:bg-gray-100"
              >
                {showCollaborators ? <EyeIcon className="w-4 h-4" /> : <EyeSlashIcon className="w-4 h-4" />}
              </button>
            </div>
            {showCollaborators && (
              <div className="space-y-2">
                {collaborators.map((collaborator) => (
                  <div key={collaborator.id} className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${collaborator.online ? 'bg-green-500' : 'bg-gray-400'}`}>
                      {collaborator.avatar}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{collaborator.name}</p>
                      <p className="text-xs text-gray-500">{collaborator.online ? 'Online' : 'Offline'}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Comments Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Comments</h3>
            <button
                onClick={() => setShowComments(!showComments)}
                className="p-1 rounded hover:bg-gray-100"
            >
                {showComments ? <EyeIcon className="w-4 h-4" /> : <EyeSlashIcon className="w-4 h-4" />}
            </button>
            </div>
            {showComments && (
              <div className="space-y-2">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-900">Great color palette! 🎨</p>
                  <p className="text-xs text-gray-500 mt-1">- Sarah Chen, 2 min ago</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-900">Love the typography choices</p>
                  <p className="text-xs text-gray-500 mt-1">- Mike Johnson, 5 min ago</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Canvas Area */}
        <div className="flex-1 flex flex-col">
          {/* Canvas Header */}
          <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Design Inspiration Board</h1>
                <p className="text-sm text-gray-600">Collaborate with your team in real-time</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex -space-x-2">
                  {collaborators.map((collaborator) => (
                    <div
                      key={collaborator.id}
                      className={`w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-medium ${collaborator.online ? 'bg-green-500' : 'bg-gray-400'}`}
                      title={collaborator.name}
                    >
                      {collaborator.avatar}
                    </div>
                  ))}
                </div>
                <button className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all duration-200 font-medium">
                  Share Board
                </button>
              </div>
            </div>
          </div>

          {/* Canvas */}
          <div className="flex-1 p-6 overflow-auto">
            <div className="relative mx-auto" style={{ width: CARD_WIDTH, height: CARD_HEIGHT }}>
              <DropZone
                width={CARD_WIDTH}
                height={CARD_HEIGHT}
                onDrop={handleDrop}
                className="bg-white rounded-xl shadow-lg border border-gray-200"
              >
                {items.map((item) => (
                <DraggableItem
                  key={item.id}
                  item={item}
                  isSelected={selectedItem === item.id}
                    onSelect={handleItemSelect}
                    onUpdate={handleItemUpdate}
                    onLayerChange={handleLayerChange}
                />
              ))}
            </DropZone>
            </div>
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

export default MoodBoard; 